/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();

      this.section = this.closest('.js-product');

      if (!this.section) return;

      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll(
        '.option-selector:not(.option-selector--custom)'
      );

      this.nativeSelector = document.getElementById(
        `variants-${this.section.dataset.section}`
      );

      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      // Cache bound event handler so the same function reference is used.
      this.onVariantChange = this.handleVariantChange.bind(this);

      this.updateAvailability();
      this.updateAddToCartButton();

      this.addEventListener('change', this.onVariantChange);

      this.applySearchParams();

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (!evt || !evt.target) return;

      /**
       * Combined listings:
       * If selection contains a URL for a different product,
       * redirect while preserving selected option IDs.
       */
      const productUrl =
        evt.target.dataset?.productUrl ||
        evt.detail?.productUrl;

      if (productUrl) {
        const selectedUrl = new URL(
          productUrl,
          window.location.origin
        );

        if (selectedUrl.pathname !== window.location.pathname) {
          const params =
            `option_values=${this.getSelectedOptionIds().join(',')}`;

          const separator = productUrl.includes('?') ? '&' : '?';

          window.location =
            `${productUrl}${separator}${params}`;

          return;
        }
      }

      /**
       * Resolve currently selected options and variant.
       */
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection =
        !this.variant &&
        this.selectedOptions.find((option) => option === null) === null;

      /**
       * Keep the original execution order.
       *
       * Do not move these into RAF/setTimeout yet.
       * Other theme/app code may depend on the current synchronous order.
       */
      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();

      VariantPicker.updateLabelText(evt);

      /**
       * Keep this event synchronous for compatibility with:
       * - gallery
       * - sticky ATC
       * - product form
       * - app/theme integrations
       */
      this.dispatchEvent(
        new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions
          }
        })
      );
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;

      if (this.selectedOptions.indexOf(null) > -1) {
        return;
      }

      this.addBtn =
        this.addBtn ||
        this.productForm.querySelector('[name="add"]');

      if (!this.addBtn) return;

      const variantAvailable =
        Boolean(this.variant && this.variant.available);

      const unavailableStr =
        this.variant
          ? theme.strings.noStock
          : theme.strings.noVariant;

      const nextDisabled = !variantAvailable;

      const nextText = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;

      /**
       * Avoid unnecessary DOM writes.
       */
      if (this.addBtn.disabled !== nextDisabled) {
        this.addBtn.disabled = nextDisabled;
      }

      if (this.addBtn.textContent !== nextText) {
        this.addBtn.textContent = nextText;
      }
    }

    /**
     * Updates the availability status of an option.
     *
     * @param {Element} optionEl
     * @param {boolean} exists
     * @param {boolean} available
     */
    static updateOptionAvailability(
      optionEl,
      exists,
      available
    ) {
      if (!optionEl) return;

      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;

      /**
       * Avoid writing identical classes repeatedly.
       */
      const shouldBeUnavailable = !available;
      const shouldBeNonexistent = !exists;

      if (
        optionEl.classList.contains('is-unavailable') !==
        shouldBeUnavailable
      ) {
        optionEl.classList.toggle(
          'is-unavailable',
          shouldBeUnavailable
        );
      }

      if (
        optionEl.classList.contains('is-nonexistent') !==
        shouldBeNonexistent
      ) {
        optionEl.classList.toggle(
          'is-nonexistent',
          shouldBeNonexistent
        );
      }

      /**
       * Dropdown option.
       */
      if (
        optionEl.classList.contains('custom-select__option')
      ) {
        const em = optionEl.querySelector('em');

        if (em) {
          if (em.hidden !== available) {
            em.hidden = available;
          }

          if (!available && em.textContent !== unavailableText) {
            em.textContent = unavailableText;
          }

          return;
        }

        /**
         * Preserve existing theme behaviour:
         * create <em> only when it does not already exist.
         */
        if (!available) {
          optionEl.insertAdjacentHTML(
            'beforeend',
            ` <em class="pointer-events-none">${unavailableText}</em>`
          );
        }

        return;
      }

      /**
       * Radio / swatch option.
       */
      const label = optionEl.nextElementSibling;

      if (!label) return;

      if (!available) {
        if (label.title !== unavailableText) {
          label.title = unavailableText;
        }
      } else if (label.hasAttribute('title')) {
        label.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') {
        return;
      }

      const variants = this.data.variants;
      const selectedOptions = this.selectedOptions;

      this.optionSelectors.forEach(
        (selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );

          let optionThatExists = false;

          options.forEach((option) => {
            /**
             * Skip combined listings.
             */
            if (option.dataset.productUrl) {
              return;
            }

            const optionValue =
              selector.dataset.selectorType === 'dropdown'
                ? option.dataset.value
                : option.value;

            let variantsExist = false;
            let variantsAvailable = false;

            /**
             * Optimized variant scan.
             *
             * Original code checked every variant even after an
             * available matching variant had already been found.
             *
             * Once both exists + available are true there is no
             * reason to inspect additional variants.
             */
            for (
              let variantIndex = 0;
              variantIndex < variants.length;
              variantIndex += 1
            ) {
              const variant = variants[variantIndex];

              let matches = true;

              for (
                let previousIndex = 0;
                previousIndex < selectorIndex;
                previousIndex += 1
              ) {
                const selectedValue =
                  selectedOptions[previousIndex];

                if (
                  selectedValue !== null &&
                  variant.options[previousIndex] !==
                    selectedValue
                ) {
                  matches = false;
                  break;
                }
              }

              if (!matches) continue;

              if (
                variant.options[selectorIndex] !==
                optionValue
              ) {
                continue;
              }

              variantsExist = true;

              if (!optionThatExists) {
                optionThatExists = option;
              }

              if (variant.available) {
                variantsAvailable = true;

                /**
                 * Nothing else can improve the result.
                 */
                break;
              }
            }

            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });

          /**
           * Preserve prune behaviour exactly.
           */
          if (
            this.dataset.availability === 'prune' &&
            optionThatExists &&
            selector.querySelector(
              '.js-option.is-nonexistent:is(:checked, [aria-selected="true"])'
            )
          ) {
            setTimeout(() => {
              const customSelect =
                optionThatExists.closest('custom-select');

              if (customSelect) {
                customSelect.selectOption(optionThatExists);
              } else {
                optionThatExists.click();
              }
            }, 0);
          }
        }
      );
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder ||
        this.section.querySelector('.backorder');

      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formattedVariant =
          this.data.formatted[this.variant.id];

        if (formattedVariant) {
          const { inventory } = formattedVariant;

          if (
            this.variant.inventory_management &&
            inventory === 'none'
          ) {
            this.backorderProduct =
              this.backorderProduct ||
              this.backorder.querySelector(
                '.backorder__product'
              );

            this.productTitle =
              this.productTitle ||
              this.section.querySelector('.product-title');

            if (
              this.backorderProduct &&
              this.productTitle
            ) {
              const variantTitle =
                this.variant.title.includes('Default')
                  ? ''
                  : ` - ${this.variant.title}`;

              const nextText =
                `${this.productTitle.textContent}${variantTitle}`;

              if (
                this.backorderProduct.textContent !==
                nextText
              ) {
                this.backorderProduct.textContent =
                  nextText;
              }
            }

            hideBackorder = false;
          }
        }
      }

      if (this.backorder.hidden !== hideBackorder) {
        this.backorder.hidden = hideBackorder;
      }
    }

    /**
     * Updates colour option label text.
     *
     * @param {object} evt
     */
    static updateLabelText(evt) {
      if (!evt || !evt.target) return;

      const selector =
        evt.target.closest('.option-selector');

      if (!selector) return;

      if (selector.dataset.selectorType === 'dropdown') {
        return;
      }

      const colorText =
        selector.querySelector('.js-color-text');

      if (!colorText) return;

      const label = evt.target.nextElementSibling;

      if (!label) return;

      const value =
        label.querySelector('.js-value');

      if (!value) return;

      const nextText = value.textContent;

      if (colorText.textContent !== nextText) {
        colorText.textContent = nextText;
      }
    }

    /**
     * Updates pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector(
          'pickup-availability'
        );

      if (!this.pickUpAvailability) {
        return;
      }

      if (this.variant && this.variant.available) {
        /**
         * Preserve original behaviour.
         *
         * Do not debounce this until pickup component behaviour
         * has been reviewed.
         */
        this.pickUpAvailability.getAvailability(
          this.variant.id
        );
      } else {
        if (
          this.pickUpAvailability.hasAttribute(
            'available'
          )
        ) {
          this.pickUpAvailability.removeAttribute(
            'available'
          );
        }

        if (this.pickUpAvailability.innerHTML !== '') {
          this.pickUpAvailability.innerHTML = '';
        }
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price ||
        this.section.querySelector(
          '.product-info__price .price'
        );

      if (!this.price) return;

      let { variant } = this;

      /**
       * Find minimum priced variant during pre-selection.
       */
      if (this.preSelection) {
        variant = this.data.variants[0];

        for (
          let i = 1;
          i < this.data.variants.length;
          i += 1
        ) {
          if (
            this.data.variants[i].price <
            variant.price
          ) {
            variant = this.data.variants[i];
          }
        }
      }

      if (variant) {
        this.priceCurrentEl =
          this.priceCurrentEl ||
          this.price.querySelector('.price__current');

        this.priceWasEl =
          this.priceWasEl ||
          this.price.querySelector('.price__was');

        this.unitPriceEl =
          this.unitPriceEl ||
          this.price.querySelector('.unit-price');

        const formatted =
          this.data.formatted[variant.id];

        if (
          this.priceCurrentEl &&
          this.priceWasEl &&
          formatted
        ) {
          const currentLabel =
            this.priceCurrentEl.querySelector(
              '.js-label'
            );

          const currentValue =
            this.priceCurrentEl.querySelector(
              '.js-value'
            );

          const wasLabel =
            this.priceWasEl.querySelector(
              '.js-label'
            );

          const wasValue =
            this.priceWasEl.querySelector(
              '.js-value'
            );

          if (
            variant.compare_at_price >
            variant.price
          ) {
            if (
              currentLabel &&
              currentLabel.textContent !==
                theme.strings.salePrice
            ) {
              currentLabel.textContent =
                theme.strings.salePrice;
            }

            if (
              currentValue &&
              currentValue.innerHTML !==
                formatted.price
            ) {
              currentValue.innerHTML =
                formatted.price;
            }

            if (
              wasLabel &&
              wasLabel.textContent !==
                theme.strings.regularPrice
            ) {
              wasLabel.textContent =
                theme.strings.regularPrice;
            }

            const compareAtPrice =
              formatted.compareAtPrice || '';

            if (
              wasValue &&
              wasValue.innerHTML !==
                compareAtPrice
            ) {
              wasValue.innerHTML =
                compareAtPrice;
            }
          } else {
            if (
              currentLabel &&
              currentLabel.textContent !==
                theme.strings.regularPrice
            ) {
              currentLabel.textContent =
                theme.strings.regularPrice;
            }

            if (
              currentValue &&
              currentValue.innerHTML !==
                formatted.price
            ) {
              currentValue.innerHTML =
                formatted.price;
            }

            if (
              wasLabel &&
              wasLabel.textContent !== ''
            ) {
              wasLabel.textContent = '';
            }

            if (
              wasValue &&
              wasValue.innerHTML !== ''
            ) {
              wasValue.innerHTML = '';
            }
          }

          /**
           * Unit price.
           */
          if (variant.unit_price_measurement) {
            if (this.unitPriceEl) {
              const valueEl =
                this.unitPriceEl.querySelector(
                  '.unit-price__price'
                );

              const unitEl =
                this.unitPriceEl.querySelector(
                  '.unit-price__unit'
                );

              const value =
                variant.unit_price_measurement
                  .reference_value;

              const unit =
                variant.unit_price_measurement
                  .reference_unit;

              const unitText =
                value === 1
                  ? unit
                  : `${value} ${unit}`;

              if (
                valueEl &&
                valueEl.innerHTML !==
                  formatted.unitPrice
              ) {
                valueEl.innerHTML =
                  formatted.unitPrice;
              }

              if (
                unitEl &&
                unitEl.textContent !== unitText
              ) {
                unitEl.textContent = unitText;
              }

              if (this.unitPriceEl.hidden) {
                this.unitPriceEl.hidden = false;
              }
            }
          } else if (
            this.unitPriceEl &&
            !this.unitPriceEl.hidden
          ) {
            this.unitPriceEl.hidden = true;
          }
        }

        const onSale =
          variant.compare_at_price > variant.price;

        const soldOut =
          !variant.available &&
          !this.preSelection;

        if (
          this.price.classList.contains(
            'price--on-sale'
          ) !== onSale
        ) {
          this.price.classList.toggle(
            'price--on-sale',
            onSale
          );
        }

        if (
          this.price.classList.contains(
            'price--sold-out'
          ) !== soldOut
        ) {
          this.price.classList.toggle(
            'price--sold-out',
            soldOut
          );
        }
      }

      this.priceDefault =
        this.priceDefault ||
        this.price.querySelector('.price__default');

      this.priceNoVariant =
        this.priceNoVariant ||
        this.price.querySelector(
          '.price__no-variant'
        );

      this.priceFrom =
        this.priceFrom ||
        this.price.querySelector('.price__from');

      const hideDefault =
        !this.variant &&
        !this.preSelection;

      if (
        this.priceDefault &&
        this.priceDefault.hidden !== hideDefault
      ) {
        this.priceDefault.hidden = hideDefault;
      }

      const hideNoVariant =
        Boolean(
          this.variant ||
          this.preSelection
        );

      if (
        this.priceNoVariant &&
        this.priceNoVariant.hidden !== hideNoVariant
      ) {
        this.priceNoVariant.hidden =
          hideNoVariant;
      }

      if (this.priceFrom) {
        const hideFrom = !this.preSelection;

        if (this.priceFrom.hidden !== hideFrom) {
          this.priceFrom.hidden = hideFrom;
        }
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku =
        this.sku ||
        this.section.querySelector(
          '.product-sku__value'
        );

      if (!this.sku) return;

      const skuAvailable =
        this.variant &&
        this.variant.sku;

      const nextSku =
        skuAvailable
          ? this.variant.sku
          : '';

      if (this.sku.textContent !== nextSku) {
        this.sku.textContent = nextSku;
      }

      const hideSku = !skuAvailable;

      if (
        this.sku.parentNode &&
        this.sku.parentNode.hidden !== hideSku
      ) {
        this.sku.parentNode.hidden = hideSku;
      }
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode =
        this.barcode ||
        this.section.querySelector(
          '.product-barcode__value'
        );

      if (!this.barcode) return;

      const barcodeAvailable =
        this.variant &&
        this.variant.barcode;

      const nextBarcode =
        barcodeAvailable
          ? this.variant.barcode
          : '';

      if (
        this.barcode.textContent !==
        nextBarcode
      ) {
        this.barcode.textContent =
          nextBarcode;
      }

      const hideBarcode =
        !barcodeAvailable;

      if (
        this.barcode.parentNode &&
        this.barcode.parentNode.hidden !==
          hideBarcode
      ) {
        this.barcode.parentNode.hidden =
          hideBarcode;
      }
    }

    /**
     * Updates the URL with the selected variant id.
     *
     * IMPORTANT:
     * This remains synchronous intentionally.
     *
     * Google / Clarity / Shopify may hook history.replaceState,
     * but moving it outside the interaction could change integration
     * timing. We will only do that after reviewing main.js.
     *
     * @param {object} evt
     */
    updateUrl(evt) {
      if (
        !evt ||
        evt.type !== 'change' ||
        this.dataset.updateUrl === 'false'
      ) {
        return;
      }

      const url = this.variant
        ? `${this.dataset.url}?variant=${this.variant.id}`
        : this.dataset.url;

      /**
       * Safe INP optimization:
       * Do not call history.replaceState if browser already has
       * exactly the same product path + query.
       *
       * This avoids unnecessary tracking hooks for duplicate events.
       */
      try {
        const nextUrl = new URL(
          url,
          window.location.origin
        );

        const currentPath =
          `${window.location.pathname}${window.location.search}`;

        const nextPath =
          `${nextUrl.pathname}${nextUrl.search}`;

        if (currentPath === nextPath) {
          return;
        }
      } catch (error) {
        // Keep original behaviour if URL parsing fails.
      }

      window.history.replaceState(
        {},
        '',
        url
      );
    }

    /**
     * Updates hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(
          '.js-product-form, .js-instalments-form'
        );

      const nextValue = this.variant
        ? String(this.variant.id)
        : '';

      this.forms.forEach((form) => {
        const input =
          form.querySelector('[name="id"]');

        if (!input) return;

        /**
         * Very important:
         *
         * Keep the original bubbling change event because other
         * theme/app integrations may depend on it.
         *
         * But never dispatch it if the value did not actually change.
         */
        if (
          String(input.value) ===
          nextValue
        ) {
          return;
        }

        input.value = nextValue;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      });
    }

    /**
     * Gets variant data.
     *
     * @returns {?object}
     */
    getVariantData() {
      const dataEl =
        this.querySelector(
          '[type="application/json"]'
        );

      if (!dataEl) {
        return {
          variants: [],
          formatted: {}
        };
      }

      return JSON.parse(
        dataEl.textContent
      );
    }

    /**
     * Get selected option values.
     *
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach(
        (selector) => {
          if (
            selector.dataset.selectorType ===
            'dropdown'
          ) {
            const selected =
              selector.querySelector(
                '.custom-select__option[aria-selected="true"]'
              );

            selectedOptions.push(
              selected &&
              selected.dataset.value !== ''
                ? selected.dataset.value
                : null
            );
          } else {
            const selected =
              selector.querySelector(
                'input:checked'
              );

            selectedOptions.push(
              selected
                ? selected.value
                : null
            );
          }
        }
      );

      return selectedOptions;
    }

    /**
     * Get selected option value IDs.
     *
     * @returns {Array}
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach(
        (selector) => {
          if (
            selector.dataset.selectorType ===
            'dropdown'
          ) {
            const selected =
              selector.querySelector(
                '.custom-select__option[aria-selected="true"]'
              );

            selectedOptionIds.push(
              selected &&
              selected.dataset.value !== ''
                ? selected.dataset.valueId
                : null
            );
          } else {
            const selected =
              selector.querySelector(
                'input:checked'
              );

            selectedOptionIds.push(
              selected
                ? selected.dataset.valueId
                : null
            );
          }
        }
      );

      return selectedOptionIds;
    }

    /**
     * Get selected variant data.
     *
     * @returns {?object}
     */
    getSelectedVariant() {
      return (
        this.data.variants.find(
          (variant) =>
            variant.options.every(
              (value, index) =>
                value ===
                this.selectedOptions[index]
            )
        ) || null
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams =
        new URLSearchParams(
          window.location.search
        );

      const optionSelectors =
        Array.from(
          this.optionSelectors
        );

      Array.from(
        searchParams.keys()
      ).forEach((key) => {
        const value =
          searchParams.get(key);

        const matchingOptionSelector =
          optionSelectors.find(
            (selector) =>
              selector.dataset.option ===
              key
          );

        if (!matchingOptionSelector) {
          return;
        }

        if (
          matchingOptionSelector.dataset
            .selectorType === 'dropdown'
        ) {
          const colorOptionDropdown =
            matchingOptionSelector.querySelector(
              `.custom-select__option[data-value="${CSS.escape(value)}"]`
            );

          if (colorOptionDropdown) {
            const customSelect =
              colorOptionDropdown.closest(
                'custom-select'
              );

            if (customSelect) {
              customSelect.selectOption(
                colorOptionDropdown
              );
            }
          }
        } else {
          /**
           * Keep selector behaviour compatible with original theme.
           *
           * CSS.escape prevents special characters inside option
           * values from breaking the selector.
           */
          const matchingInput =
            matchingOptionSelector.querySelector(
              `input[value="${CSS.escape(value)}"]`
            );

          if (matchingInput) {
            const matchingOptionValue =
              matchingOptionSelector.querySelector(
                '.option-selector__label-value'
              );

            if (
              matchingOptionValue &&
              matchingOptionValue.textContent !==
                value
            ) {
              matchingOptionValue.textContent =
                value;
            }

            if (!matchingInput.checked) {
              matchingInput.checked = true;

              matchingInput.dispatchEvent(
                new CustomEvent('change', {
                  bubbles: true,
                  cancelable: false
                })
              );
            }
          }
        }
      });
    }
  }

  customElements.define(
    'variant-picker',
    VariantPicker
  );
}